// COP — the chat capability ADAPTER (CONTRACT:C3-CAPABILITY-PLUGIN.1.0).
//
// Wave 2 retires the old clear-shell COP chat capability (which put the body in
// payload.clear.text, payload_mode:"clear") and wires the SHARED, ENCRYPTED
// @tak-tdf/cap-chat capability (CONTRACT:C3-CAPABILITY-PLUGIN.1.0) — the body is sealed
// into payload.encrypted_ref via the injected CONTRACT:I5-KEY-ACCESS.1.0 port
// (chatKeyAccess), so an unauthorized reader gets the SHELL not the BODY.
//
// This module is the thin COP-side ADAPTER between the cap-chat ChatCapability and the
// React ChatPanel: it builds the cap-chat capability with the COP's chatKeyAccess +
// logged-in persona subject, and exposes a narrow panel-facing ChatApi (rooms / messages
// / send / subscribe) plus the R2 identity-switch rebuild hook the host drives. It never
// enters the render loop and holds no React.
//
// ROOM MODEL (§3, L2 — CEILING semantics, flipped 2026-07-06): a room's creator-set
// classification is the SEAL class for ALL room traffic and the CEILING for a message's
// content pick (type at-or-below the room class, never above — ROOM_CEILING); a chat-room
// DESCRIPTOR envelope makes a zero-message room listable.
// GLOMAR (§4, C4): rooms() folds STRICTLY off the post-PDP-seam ingested envelopes —
// a denied room is ABSENT (no row, no badge, no presence).
//
// WAVE 3a (C5, CHAT-BUILD-SPEC §5): this adapter ALSO owns the "Plaintext Bridge" TOGGLE
// STATE MACHINE — the LEGACY-DEGRADATION logic, the ONLY sanctioned clear-send path. It is
// momentary, one-shot, auto-reverting, structurally fail-closed, and FULLY AUDITED:
//   - DEFAULT ALWAYS SEALS: send() routes to the sealed cap-chat path for 100% of normal
//     traffic (L3). Only an ARMED bridge flips THAT ONE envelope to the clear build path.
//   - ARM is reachable ONLY when roomLegacyPresence(currentRoom) is true; armBridge()
//     RE-CHECKS it and refuses otherwise (never an ambient default, never remote-triggered).
//   - ONE-SHOT: a SINGLE authoritative "consumed" transition auto-reverts after the FIRST
//     send OR an injectable timer OR a room switch OR any legacy/clearance state change.
//     Never sticky, NO persisted state (a reload comes back encrypted).
//   - FAIL-CLOSED INDEPENDENT OF THE TOGGLE (§5.5): the room-marking gate (the class
//     CEILING + the releasability clamp) still runs on the armed clear envelope —
//     plaintext can never ORIGINATE above the room class.
//   - AUDIT (§5.6): every ARM / PLAINTEXT SEND / EXPIRY-NO-SEND appends a PLAINTEXT_BRIDGE
//     row to the Seam-B ledger via the injected recordBridgeAudit hook (cop.ts).

import type {
  Capability,
  CanonicalEnvelope,
  HostContext,
  PanelSpec,
  Disposable,
} from "../../../../packages/contracts-ts/src/index.ts";
import {
  ChatCapability,
  createChatCapability as createCapChatCapability,
  ALL_CHAT_ROOMS,
  isChatMessage,
  isRoomDescriptor,
  roomPolicyRef,
  PRESENCE_AUTHOR,
  type ChatMessage,
  type RoomRow,
  type ChatEmitFn,
} from "../../../../packages/cap-chat/src/index.ts";
// DM room-key helpers come straight from the model module (the package index barrel is
// orchestrator-owned; these additive helpers live with the model they belong to).
import { dmRoomKey, isDmRoom, dmPeer } from "../../../../packages/cap-chat/src/messages.ts";
import type { ChatKeyAccess } from "../host/chatKeyAccess.ts";
// TYPE-ONLY import (erased at runtime) — the adapter's static graph stays kas-free for the
// node-side tsx checks; the live hooks are INJECTED via CopChatCapabilityOptions (cop.ts).
import type { AuthTokenState } from "../host/kas.ts";
import { makeChatEmit } from "../host/emitClient.ts";
// CONTRACT:C3-CHAT.1.0 — the pure @mention selectors (UX over the CLEAR shell, NO crypto).
// Deep-imported from the model module (NOT the package barrel — the barrel is orchestrator-
// owned) and re-exported so the ChatPanel imports the whole chat seam from this one adapter.
// (FOLLOW-UP for the orchestrator: surface these from packages/cap-chat/src/index.ts too.)
import {
  parseMentions,
  resolveMentions,
  mentionsSubject,
  tokenizeMentions,
  type MentionSegment,
} from "../../../../packages/cap-chat/src/messages.ts";

/** Stable capability id (matches cap-chat's default). */
export const CHAT_CAPABILITY_ID = "scop:cap:chat";

export { ALL_CHAT_ROOMS, parseMentions, resolveMentions, mentionsSubject, tokenizeMentions, isDmRoom, dmPeer };
export type { ChatMessage, RoomRow, MentionSegment };

/** The narrow API the ChatPanel consumes (Wave 3 rewrites the panel against this).
 *  Decoupled from the capability class so the panel depends only on this surface,
 *  never on HostContext or the cap-chat internals. */
export interface ChatApi {
  /** The room this client is currently in (maps to a mission). */
  readonly room: string;
  /** The acting subject's author id (used as the sender identity). */
  readonly author: string;
  /** Current ordered snapshot (oldest → newest) of a room (default: current room). */
  messages(room?: string): ChatMessage[];
  /** GLOMAR room list (C4): a pure fold of the post-PDP-seam ingested envelopes. A
   *  denied room is ABSENT — no row. "All Chat Rooms" is the fixed UNCLASSIFIED room. */
  rooms(): RoomRow[];
  /** Open / switch to a room (publishes a descriptor on first observe so a zero-message
   *  room is listable). The creator-set classification is the room marking (L2). */
  openRoom(room: string, classification?: string, releasability?: string[]): Promise<void>;
  /** Send a message. Enforces the room CEILING (the pick must be <= the room class —
   *  throws ROOM_CEILING above it), seals the body (with the pick riding inside as the
   *  content marking) via I5 (R1 async) at EXACTLY the room class, and publishes. Rejects
   *  (keeps the draft) if the gate/seal fails.
   *
   *  PLAINTEXT BRIDGE (C5, §5.4): if the bridge is ARMED for the current room, THIS ONE
   *  send takes the CLEAR build path (body in payload.clear.text) and the bridge auto-reverts
   *  (one-shot). EVERY other send stays sealed. The room-marking gate STILL runs on the
   *  clear envelope (fail-closed independent of the toggle) — a refused plaintext send keeps
   *  the draft AND still consumes the one-shot. */
  send(text: string, room?: string, classification?: string, releasability?: string[]): Promise<void>;
  /** Subscribe to store changes. Fires immediately with the current state, then on
   *  every ingest/send/identity-switch. Returns an unsubscribe. */
  subscribe(cb: () => void): () => void;

  // --- READ-STATE + CHANNEL ADD/REMOVE (CONTRACT:C3-CHAT.1.0) -------------------------------

  /** Unread count for a room (default current) — the per-conversation badge (§7.7). Folds off
   *  the post-seam store; a soft-left (hidden) room reads 0. Never counts hidden/denied traffic. */
  unreadCount(room?: string): number;
  /** Total unread across every VISIBLE (non-hidden) room — the global badge on the chat entry. */
  totalUnread(): number;
  /** Mark a room (default current) READ — clears its unread badge (§7.7 mark-read-on-open). */
  markRoomRead(room?: string): void;
  /** Mark EVERY visible room READ (bulk "mark all read"). */
  markAllRoomsRead(): void;
  /** Can this room be LEFT/hidden? false for the broadcast room + mandatory classification-floor
   *  rooms; true for optional rooms (ASYNC-BUNDLES Decisions log). Mirrors RoomRow.mandatory. */
  canLeaveRoom(room: string): boolean;
  /** SOFT-LEAVE: hide an OPTIONAL room from THIS client's switcher (rejoin via openRoom). A
   *  local view filter only (Glomar-safe — only ever removes an already-cleared row). REFUSES a
   *  mandatory room (no-op). Reverts an armed bridge scoped to that room first. */
  leaveRoom(room: string): void;

  // --- DIRECT MESSAGES (DM = a room with one other person) — built on openRoom -------------

  /** Open (creating if needed) the canonical 1:1 DM room with `otherId`. A DM is an ordinary
   *  room (mission) at the UNCLASSIFIED class whose key encodes BOTH ids order-independently,
   *  so both peers converge on the same room. Adds NO new crypto/policy axis — transport
   *  privacy (per-pair releasability) is the separate KV-ABAC AOA, not this. */
  openDm(otherId: string): Promise<void>;
  /** The distinct people known from PRESENCE across the post-seam rooms (minus self) — the
   *  source for the "message someone" / DM picker. A Glomar-safe fold: only people present in
   *  rooms you can already see appear (never a hidden roster). */
  people(): string[];
  /** Known people WITH display callsigns when known (client-asserted; display-only) — the
   *  callsign-aware DM-picker/autocomplete + message-row display source. Same Glomar-safe
   *  fold as people(). */
  contacts(): { id: string; callsign?: string }[];

  // --- HONEST SESSION STATE + SEALED-ROW RECOVERY ------------------------------------------

  /** The live access-token state ("none"|"ok"|"expiring"|"expired"). An EXPIRED token 401s
   *  at the KAS and would otherwise masquerade as clearance denials ("body sealed" on your
   *  own traffic) — the panel renders an honest session banner off this instead. Falls back
   *  to "ok" when the host injected no hook (tests/headless). */
  authState(): AuthTokenState;
  /** Can this session silently renew (persona/password sessions with a refresh token)?
   *  FALSE for federated (Google) sessions — idplite deliberately issues no refresh token
   *  there (re-auth goes back through Google), so the UI directs a re-login instead. */
  canRenew(): boolean;
  /** Re-ask the KAS for every still-sealed row (idempotent + fail-closed: a genuine
   *  clearance denial stays denied; only rows sealed by an expired session recover).
   *  Auto-fired when the silent refresh adopts a fresh token; also a manual affordance. */
  retryRedacted(): Promise<number>;

  // --- THE PLAINTEXT-BRIDGE TOGGLE (C5, §5.3/§5.4) -----------------------------------------

  /** Is the given (or current) room's bridge REACHABLE — i.e. is a non-stale LEGACY client
   *  genuinely present (§5.2)? The Wave-3b composer enables the bridge control ONLY when
   *  true. A pure fold off the LIVE roster — re-derived every call. */
  bridgeReachable(room?: string): boolean;
  /** The non-stale legacy callsigns in the given (or current) room (for the confirm modal +
   *  the armed banner). Folds off the SAME roster as `bridgeReachable`. */
  legacyCallsigns(room?: string): string[];
  /** ARM the one-shot bridge for the current room — the POST-CONFIRM entry point (the
   *  per-message confirm modal is Wave-3b UI). It RE-CHECKS roomLegacyPresence and REFUSES
   *  (returns false, no state change, no audit) if no legacy is present — never armed by a
   *  remote party or an ambient default. On success: audits ARM, starts the one-shot window
   *  (timer + room/legacy/clearance-change revert), returns true. */
  armBridge(): boolean;
  /** Manually DISARM the bridge (the Wave-3b window-blur / cancel binding). A no-send revert
   *  is audited (the bridge was armed but produced no plaintext send). No-op if not armed. */
  disarmBridge(): void;
  /** Is the bridge currently ARMED (the next send will be clear)? */
  isBridgeArmed(): boolean;
}

/** A chat capability + its panel-facing API + the host-driven lifecycle hooks. The
 *  factory returns all three so the host registers the Capability, App.tsx mounts
 *  <ChatPanel api={api} />, and cop.ts drives the R2 rebuild on identity switch. */
export interface ChatCapabilityHandle {
  capability: Capability;
  api: ChatApi;
  /** R2 — IDENTITY-SWITCH REBUILD (§4.3). The host calls this on every persona switch
   *  with the NEW subject + the projection's currently-visible chat envelopes for that
   *  subject. The capability clears its decoded-body store and re-ingests ONLY those —
   *  a HIGH body decoded under HIGH VANISHES on a switch-to-LOW. (The chatKeyAccess
   *  plaintext cache is cleared in lockstep by setKasToken.) */
  rebuildForSubject(
    subject: { id: string } & Record<string, unknown>,
    visible: CanonicalEnvelope[],
  ): Promise<void>;
}

/** Which bridge action an audit row records (C5, §5.6). */
export type BridgeAuditKind = "ARM" | "PLAINTEXT_SEND" | "EXPIRY_NO_SEND";

/** The structured audit event the adapter hands the host for a PLAINTEXT_BRIDGE row. The
 *  host (cop.ts) maps it onto recordAudit("PLAINTEXT_BRIDGE", subject, policyRef) — the
 *  adapter NEVER touches the ledger directly (Rule 4: verdicts live only in the Audit pane +
 *  Trust modal). Carries everything §5.6 mandates: room/policyRef, target legacy callsigns,
 *  message id, and the action kind; the actor (live persona) + Zulu time are stamped host-side. */
export interface BridgeAuditEvent {
  /** ARM / PLAINTEXT_SEND / EXPIRY_NO_SEND. */
  kind: BridgeAuditKind;
  /** The room the bridge was armed for. */
  room: string;
  /** The room's policy ref (deterministic per room). */
  policyRef: string;
  /** The non-stale legacy callsigns the bridge targets (captured at ARM time). */
  targetLegacy: string[];
  /** The message id of the clear send (PLAINTEXT_SEND only; undefined otherwise). */
  messageId?: string;
  /** Why the bridge reverted with no send (EXPIRY_NO_SEND only): "timeout" | "room-switch"
   *  | "state-change" | "manual" | "marking-refused" (the room-marking gate — ROOM_CEILING /
   *  rel fail-closed — refused the armed clear send; formerly "floor-refused"). */
  reason?: string;
}

/** Options for the COP chat capability adapter. */
export interface CopChatCapabilityOptions {
  /** The COP-side I5 chat key-access port (seal/rewrap/open over the live KAS). */
  keyAccess: ChatKeyAccess;
  /** The logged-in persona — the seal/rewrap subject + author identity (one identity
   *  drives I4 and I5). Must carry an `id`. */
  subject: { id: string } & Record<string, unknown>;
  /** Initial room (default ALL_CHAT_ROOMS). */
  initialRoom?: string;
  /** AUDIT HOOK (C5, §5.6): the host's append-only PLAINTEXT_BRIDGE recorder. Called on
   *  every ARM / PLAINTEXT_SEND / EXPIRY_NO_SEND. The adapter NEVER writes the ledger
   *  directly. Optional so a test/headless build can omit it (the bridge still works). */
  recordBridgeAudit?: (ev: BridgeAuditEvent) => void;
  /** ONE-SHOT auto-revert window in ms (default 20_000, §5.3). */
  bridgeTimeoutMs?: number;
  /** LIVE getter for the operator's display callsign (the settings `user.callsign` leaf) —
   *  threaded into cap-chat so sends/presence carry it (display-only, client-asserted). */
  callsign?: () => string | undefined;
  /** OPTIONAL demo-tier message signer (getter) — the COP boot wires a WebCrypto session signer
   *  so sends are seal-bound-signed (display/trust only; omit → unsigned "asserted"). */
  signer?: () => import("../../../../packages/cap-chat/src/messages.ts").MessageSigner | undefined;
  /** INJECTED session-state hooks (host/kas.ts — injected, not imported, so this adapter's
   *  static graph stays kas-free for the node-side tsx checks). All optional: tests/headless
   *  builds omit them and the api falls back to "ok" / no-renew / no-op. */
  authState?: () => AuthTokenState;
  canRenew?: () => boolean;
  onTokenRenewed?: (fn: () => void) => () => void;
  /** INJECTABLE CLOCK (offline-testable, §5.3): the timer primitive. Defaults to
   *  window.setTimeout/clearTimeout. A test injects a fake clock so the 20s revert fires
   *  WITHOUT a real wait. `set` returns an opaque handle `clear` cancels. */
  clock?: {
    set(cb: () => void, ms: number): unknown;
    clear(handle: unknown): void;
  };
  /** OPTIONAL OUTBOUND CoT EGRESS hook (S1-TAK-GATEWAY) — crosses a published chat envelope
   *  to the TAK net via the gateway POST /emit. Defaults to `makeChatEmit()`, which returns
   *  `undefined` unless egress is ARMED (VITE_EMIT=1 / ?emit=1) — so by default there is NO
   *  egress and the in-process chat is untouched. A test injects a spy (or `null` to force
   *  egress OFF) — pass `null` to suppress the default wiring entirely. */
  emit?: ChatEmitFn | null;
}

/** Is this envelope one the chat capability owns (a message OR a room descriptor)? Used
 *  by the host to fold the projection's currently-visible set down to chat envelopes for
 *  the R2 rebuild — so a denied room never reaches the rebuild. */
export function isChatEnvelope(env: CanonicalEnvelope): boolean {
  return isChatMessage(env) || isRoomDescriptor(env);
}

/** LAZY panel mount (C6). The React renderer (mountChatPanel.tsx → ChatPanel.tsx → its
 *  CSS) is pulled in via a DYNAMIC import so this adapter module stays React/CSS-FREE in
 *  its static graph — node-side tsx checks (bridge-toggle-check etc.) that import this
 *  adapter must not transitively load a `.css` (no CSS loader under tsx). The import runs
 *  ONLY when <PanelHost> actually mounts the panel (browser, where Vite resolves CSS).
 *  Returns a synchronous teardown that runs the real cleanup once the import resolves. */
function mountChatPanelLazy(container: unknown, api: ChatApi): () => void {
  let realCleanup: (() => void) | null = null;
  let torn = false;
  void import("../components/mountChatPanel.tsx")
    .then(({ mountChatPanel }) => {
      if (torn) return; // unmounted before the import resolved — don't mount.
      realCleanup = mountChatPanel(container, api);
    })
    .catch((e: unknown) => {
      // A failed dynamic import must not take down the host (PanelHost also isolates this) —
      // but LOG it (NIT) so a real render regression is debuggable instead of a silent blank
      // dock. Crash-isolation is preserved (we still swallow the rejection after logging).
      console.error("[cop-chat] ChatPanel dynamic import failed — panel will not mount:", e);
    });
  return () => {
    torn = true;
    realCleanup?.();
  };
}

/** Create the COP chat capability adapter. Returns the Capability for the host, the
 *  ChatApi for the panel, and the R2 rebuild hook for cop.ts. */
export function createChatCapability(opts: CopChatCapabilityOptions): ChatCapabilityHandle {
  // OUTBOUND EGRESS (S1-TAK-GATEWAY): wire the same-origin /emit hook. `opts.emit === null`
  // suppresses it entirely (tests / a build that wants no egress); `undefined` (the default)
  // falls to makeChatEmit() — which itself returns undefined unless egress is ARMED
  // (VITE_EMIT=1 / ?emit=1), so by default NO POSTs are attempted (normal dev / ?mock=1 stay
  // silent). cap-chat treats an undefined hook as "no egress".
  const emit: ChatEmitFn | undefined =
    opts.emit === null ? undefined : (opts.emit ?? makeChatEmit());

  const cap: ChatCapability = createCapChatCapability({
    keyAccess: opts.keyAccess,
    subject: opts.subject,
    id: CHAT_CAPABILITY_ID,
    initialRoom: opts.initialRoom ?? ALL_CHAT_ROOMS,
    emit,
    callsign: opts.callsign,
    signer: opts.signer,
  });

  // SAME-PERSONA SESSION RECOVERY: when the host's silent refresh adopts a fresh token,
  // re-ask the KAS for every still-sealed row. Rows sealed ONLY by the expired session
  // recover; genuine clearance denials stay denied (the KAS re-decides every ref —
  // fail-closed is untouched). Unsubscribed on deactivate.
  const offTokenRenewed = opts.onTokenRenewed?.(() => void cap.retryRedacted());

  // ------------------------------------------------------------------------------------------
  // THE PLAINTEXT-BRIDGE TOGGLE STATE MACHINE (C5, §5.3/§5.4)
  // ------------------------------------------------------------------------------------------
  //
  // STATES:  DISARMED  ──armBridge()──▶  ARMED  ──(send | timer | room-switch | state-change)──▶  DISARMED
  //
  // The bridge is a MOMENTARY, ONE-SHOT, SINGLE-WINDOW mode. There is exactly ONE in-flight
  // arm at a time, captured in `armed`. A SINGLE AUTHORITATIVE "consumed" transition
  // (`consume()`) flips ARMED→DISARMED atomically and cancels the timer — so a send in flight
  // when the timer fires can NEVER be both armed-and-reverted (whoever calls consume() first
  // wins; the loser sees `armed===null` and no-ops). NO persisted state: a reload starts
  // DISARMED (this is in-memory only), so a crash/reload always comes back encrypted.
  const bridgeTimeoutMs = opts.bridgeTimeoutMs ?? 20_000;
  const clock = opts.clock ?? {
    set: (cb: () => void, ms: number): unknown => window.setTimeout(cb, ms),
    clear: (h: unknown): void => window.clearTimeout(h as number),
  };
  const audit = opts.recordBridgeAudit;

  /** The single in-flight arm, or null when DISARMED. Captures the room + target legacy
   *  callsigns at ARM time so an audited SEND/EXPIRY names the SAME targets the operator
   *  confirmed against (a roster change after arming cannot retarget the in-flight bridge). */
  let armed: { room: string; targetLegacy: string[]; timer: unknown } | null = null;

  /** SINGLE AUTHORITATIVE consume transition. Atomically claims + clears the current arm
   *  (returns it) or null if already DISARMED. Synchronous + side-effect-only on the
   *  `armed` cell + the timer — so two racing callers (a send vs the timer) can't both win.
   *  The FIRST caller gets the arm; the rest get null and no-op. */
  const consume = (): { room: string; targetLegacy: string[] } | null => {
    const a = armed;
    if (!a) return null;
    armed = null; // claimed — any concurrent consume() now returns null
    clock.clear(a.timer); // a send that consumed first cancels the timer; vice-versa
    return { room: a.room, targetLegacy: a.targetLegacy };
  };

  /** Revert with NO send (timeout / room-switch / state-change / manual). Consumes the arm
   *  and audits an EXPIRY_NO_SEND row (the bridge was armed but produced no plaintext). A
   *  no-op if already DISARMED — so a room switch AFTER a send (which already consumed) does
   *  not double-audit. */
  const revertNoSend = (reason: string): void => {
    const a = consume();
    if (!a) return;
    audit?.({
      kind: "EXPIRY_NO_SEND",
      room: a.room,
      policyRef: roomPolicyRef(a.room),
      targetLegacy: a.targetLegacy,
      reason,
    });
    cap.notifyBridgeChange();
  };

  const api: ChatApi = {
    get room() {
      return cap.room;
    },
    get author() {
      return cap.author;
    },
    messages(room?: string): ChatMessage[] {
      return cap.view(room ?? cap.room).messages;
    },
    rooms(): RoomRow[] {
      return cap.rooms();
    },
    openRoom(room, classification, releasability): Promise<void> {
      // ROOM SWITCH auto-reverts the bridge (§5.3) — ANY openRoom (a switch OR a re-open of
      // the same room) always lands DISARMED (default-to-sealing). Revert BEFORE the switch
      // so the in-flight arm (scoped to the prior room) can never leak into the new room.
      if (armed) revertNoSend("room-switch");
      return cap.openRoom(room, classification, releasability);
    },
    async send(text, room, classification, releasability): Promise<void> {
      const target = room ?? cap.room;
      // ONE-SHOT / NEVER-STICKY INVARIANT (§5.3): ANY send that is NOT the EXACT armed-(room)
      // message disarms the one-shot. So if the bridge is armed for room A but this send goes
      // to a DIFFERENT room B (the public ChatApi.send accepts an explicit room — the Wave-3b
      // composer will exercise it), REVERT the stale arm BEFORE the sealed fallthrough — the
      // single operator confirm must never stay loaded across an unrelated send (else a later
      // send to A could fire CLEAR with no fresh confirm). The mismatch send itself stays SEALED.
      if (armed && armed.room !== target) {
        revertNoSend("room-switch");
      }
      // ENGINE BINDING (§5.4): only an ARMED bridge SCOPED TO THIS ROOM flips this one
      // envelope to clear. Every other send takes the sealed path.
      if (armed && armed.room === target) {
        // SINGLE consume transition FIRST (synchronous, before any await) so a timer firing
        // mid-send cannot double-revert: we own the arm now; the timer sees null and no-ops.
        const a = consume();
        cap.notifyBridgeChange();
        if (a) {
          // FAIL-CLOSED INDEPENDENT OF THE TOGGLE (§5.5): sendClear STILL runs the
          // room-marking gate; a pick ABOVE the room class THROWS (ROOM_CEILING) and NO
          // clear envelope is published. The one-shot is already consumed (you re-confirm
          // to bridge again).
          try {
            const messageId = await cap.sendClear(text, target, classification, releasability);
            audit?.({
              kind: "PLAINTEXT_SEND",
              room: a.room,
              policyRef: roomPolicyRef(a.room),
              targetLegacy: a.targetLegacy,
              messageId,
            });
          } catch (e) {
            // Refused (the room-marking gate: ROOM_CEILING / the rel fail-closed) — no
            // plaintext went out. Audit the consumed no-send so the arm is never silently
            // spent, then re-throw so the panel keeps the draft.
            audit?.({
              kind: "EXPIRY_NO_SEND",
              room: a.room,
              policyRef: roomPolicyRef(a.room),
              targetLegacy: a.targetLegacy,
              reason: "marking-refused",
            });
            throw e;
          }
          return;
        }
      }
      // DEFAULT: the SEALED cap-chat path — 100% of normal traffic (L3).
      await cap.send(text, target, classification, releasability);
    },
    subscribe(cb: () => void): () => void {
      const off = cap.onChange(cb);
      cb(); // fire once with the current state
      return off;
    },

    // --- READ-STATE + CHANNEL ADD/REMOVE + DM SURFACE (C3-CHAT) -----------------------------
    unreadCount(room?: string): number {
      return cap.unreadCount(room ?? cap.room);
    },
    totalUnread(): number {
      return cap.totalUnread();
    },
    markRoomRead(room?: string): void {
      cap.markRoomRead(room ?? cap.room);
    },
    markAllRoomsRead(): void {
      cap.markAllRoomsRead();
    },
    canLeaveRoom(room: string): boolean {
      return cap.canLeaveRoom(room);
    },
    leaveRoom(room: string): void {
      // Leaving a room is a room-scoped change — revert an armed bridge for it first
      // (default-to-sealing; the one-shot must never straddle a room going out of the list).
      if (armed && armed.room === room) revertNoSend("room-switch");
      cap.leaveRoom(room);
    },

    // --- DIRECT MESSAGES (DM = a room with one other person) --------------------------------
    openDm(otherId: string): Promise<void> {
      // A DM switch is a room switch — auto-revert any armed bridge first (the in-flight arm
      // is scoped to the prior room and must never straddle the switch), exactly like openRoom.
      if (armed) revertNoSend("room-switch");
      // CREATE-mode openRoom with an explicit UNCLASSIFIED class so the room is listable for
      // BOTH peers (publishes a descriptor + presence). The canonical key converges alice↔bob.
      return cap.openRoom(dmRoomKey(cap.author, otherId), "UNCLASSIFIED", []);
    },
    people(): string[] {
      // Distinct present participants across every room THIS subject can already see (rooms()
      // is the Glomar-filtered fold), minus self + the presence sentinel. Never a hidden roster.
      const me = cap.author;
      const set = new Set<string>();
      for (const r of cap.rooms()) {
        for (const p of r.present) {
          if (p !== me && p !== PRESENCE_AUTHOR) set.add(p);
        }
      }
      return [...set].sort();
    },
    contacts(): { id: string; callsign?: string }[] {
      return cap.contacts();
    },

    // --- HONEST SESSION STATE + SEALED-ROW RECOVERY -----------------------------------------
    authState(): AuthTokenState {
      return opts.authState?.() ?? "ok";
    },
    canRenew(): boolean {
      return opts.canRenew?.() ?? false;
    },
    retryRedacted(): Promise<number> {
      return cap.retryRedacted();
    },

    // --- THE PLAINTEXT-BRIDGE TOGGLE SURFACE (C5) -------------------------------------------
    bridgeReachable(room?: string): boolean {
      return cap.roomLegacyPresence(room ?? cap.room);
    },
    legacyCallsigns(room?: string): string[] {
      return cap.legacyCallsigns(room ?? cap.room);
    },
    armBridge(): boolean {
      const room = cap.room;
      // RE-CHECK roomLegacyPresence (§5.3): arming is reachable ONLY when a non-stale legacy
      // client is genuinely present. Refuse otherwise — no state change, no audit, no clear
      // path ever opens against a fully-capable (or empty) room. Idempotent: re-arming an
      // already-armed room is a no-op (still one window).
      if (!cap.roomLegacyPresence(room)) return false;
      if (armed && armed.room === room) return true; // already armed for this room
      // A stale arm for another room (shouldn't happen) is reverted first.
      if (armed) revertNoSend("state-change");
      const targetLegacy = cap.legacyCallsigns(room);
      // FAIL-SAFE ORDERING (T2): audit the ARM BEFORE committing the armed state. A throwing
      // audit sink then leaves the bridge DISARMED (the safe direction) rather than ARMED-but-
      // un-rendered (a stuck banner). recordAudit is synchronous + non-throwing today; this is
      // defensive. The timer is set TOGETHER with `armed` so the two can never disagree.
      audit?.({ kind: "ARM", room, policyRef: roomPolicyRef(room), targetLegacy });
      // COMMIT the arm: start the ONE-SHOT timer (injectable clock — offline-testable) and set
      // the armed cell atomically. On fire, revert with no send IF still armed (consume()
      // inside revertNoSend guards the race).
      const timer = clock.set(() => revertNoSend("timeout"), bridgeTimeoutMs);
      armed = { room, targetLegacy, timer };
      cap.notifyBridgeChange();
      return true;
    },
    disarmBridge(): void {
      revertNoSend("manual");
    },
    isBridgeArmed(): boolean {
      return armed !== null;
    },
  };

  // ANY LEGACY/CLEARANCE STATE CHANGE auto-reverts (§5.3): the capability fires onChange on
  // every roster/presence/store mutation. If the bridge is armed and its room is NO LONGER
  // legacy-reachable (the legacy client left / went stale, or the room's marking changed under
  // the operator), revert immediately — the only sanctioned clear window is gone.
  cap.onChange(() => {
    if (armed && !cap.roomLegacyPresence(armed.room)) revertNoSend("state-change");
  });

  // ------------------------------------------------------------------------------------------
  // PANEL-MOUNT WIRING (CHAT-BUILD-SPEC §6 / C6) — the PUBLIC C3 panel seam.
  // ------------------------------------------------------------------------------------------
  //
  // cap-chat is crypto-agnostic + React-free: its `activate(ctx)` registers a PanelSpec with
  // NO `mount`, so <PanelHost> (Seam B) would skip it. The COP add-in IS the React host, so
  // here — COP-side, exactly where a third-party add-in's host glue would live — we WRAP the
  // capability's activate to inject a `mount(container)` that renders <ChatPanel api={api}/>
  // (via mountChatPanel). The capability then mounts through the foundation's <PanelHost>
  // exactly as a third-party add-in would (no cop.ts/App.tsx render-tree change, no per-
  // capability host branching). We touch ONLY the chat PanelSpec (id ends `:panel`); every
  // other contribution passes through verbatim.
  const mountedCapability: Capability = {
    get id() {
      return cap.id;
    },
    activate(ctx: HostContext): void | Promise<void> {
      const wrapped: HostContext = {
        ...ctx,
        registerPanel(spec: PanelSpec): Disposable {
          // Inject the COP mount on the chat panel spec; leave any other spec untouched.
          const isChatPanel = spec.id === `${cap.id}:panel`;
          const next: PanelSpec =
            isChatPanel && typeof spec.mount !== "function"
              ? { ...spec, mount: (container: unknown) => mountChatPanelLazy(container, api) }
              : spec;
          return ctx.registerPanel(next);
        },
      };
      return cap.activate(wrapped);
    },
    deactivate(): void | Promise<void> {
      offTokenRenewed?.(); // release the kas token-renewal hook with the capability
      return cap.deactivate();
    },
  };

  return {
    capability: mountedCapability,
    api,
    rebuildForSubject(subject, visible): Promise<void> {
      // IDENTITY/CLEARANCE SWITCH auto-reverts the bridge (§5.3): a persona switch is a
      // clearance state change — the in-flight arm (scoped to the prior persona's roster +
      // clearance) must NOT survive. Revert BEFORE the rebuild so no clear path straddles the
      // switch; the new persona starts DISARMED (default-to-sealing).
      if (armed) revertNoSend("state-change");
      return cap.setSubject(subject, visible);
    },
  };
}
